import { useEffect, useState } from "react"
import type { PlanId } from "@/lib/branchTypes"
import { PLAN_KEYS, defaultGraphData } from "../../../extrapolated-graph/graphConstants"
import { HUD87_CAPABILITY_LINE_FLOOR_VALUE } from "../../../hud/variants"
import {
  sampleSeriesAt,
  toTimeValuePoints,
  type TimeValuePoint,
} from "../interpolate"
import type { StripPlotArea } from "../stripLayout"
import {
  buildLinePath,
  buildLineScreenPoints,
  type LineProjection,
  type LineSpan,
} from "../capability-line/linePaths"
import { placePlanLetter } from "./planLetterLabels"

// The all-plans preview fan (the choice-point beat), one line per plan,
// redesigned for the cramped, textured strip: the desktop fan's faint
// 1px black lines vanish against the red dither, so every line gets a
// page-colored KNOCK-OUT HALO under-stroke that carves a clean channel
// through it; all non-emphasized lines share one uniform dash (a fade
// would disappear against texture); the EMPHASIZED plan is thickest and
// solid; and the dither pulls back around the lines via a dot-size
// gradient at near-full opacity instead of a whole-field fade.

export const PLAN_PREVIEW_LINE_COLOR = "#000000"
const PLAN_PREVIEW_EMPHASIZED_STROKE_PX = 2
const PLAN_PREVIEW_LINE_STROKE_PX = 1.25
// The knock-out halo: this much wider than the line on each side.
export const PLAN_PREVIEW_HALO_EXTRA_PX = 2.5
// ONLY the emphasized plan is solid — a flat dashed line along the floor
// reads as "a plan you didn't pick", not the hero line. One SHARED dash
// so every line carries the same even rhythm and the plans are told
// apart by position alone.
const PLAN_PREVIEW_DASH = "2,3"
// While the fan is up the dither keeps ALMOST its full authored opacity;
// legibility comes from the dot-size gradient (DitherFieldCanvas).
export const CHOICE_POINT_DITHER_OPACITY_SCALE = 0.9
export const CHOICE_POINT_DOT_MIN_SCALE = 0.1
// Sized so dots midway between neighbouring fan lines sit visibly
// mid-ramp while regions far from every line reach full size.
export const CHOICE_POINT_DOT_FEATHER_PX = 24
// Fan fade duration — also paces the strip globe's choice-point fade and
// the camera's choice-point blend (cameraTweens).
export const PLAN_PREVIEW_FADE_MS = 600
// Drawn back-to-front; the emphasized line is sorted last (on top).
const PLAN_PREVIEW_ORDER: PlanId[] = ["S", "A", "B", "C", "D"]

// A plan's full displayed series: shared pre-branch history + the plan's
// own line (planBUs for B when present).
const combinedPlanSeries = (planId: PlanId): TimeValuePoint[] => {
  const planSeries =
    planId === "B" && defaultGraphData.planBUs?.length
      ? defaultGraphData.planBUs
      : defaultGraphData[PLAN_KEYS[planId]] ?? defaultGraphData.planD
  return toTimeValuePoints([...defaultGraphData.shared, ...planSeries])
}

// Built once off the module-stable data: safety-tax / alignment knobs
// are 0 in every view that shows the fan, so the raw series is right.
const PLAN_PREVIEW_SERIES = PLAN_PREVIEW_ORDER.map(planId => ({
  planId,
  series: combinedPlanSeries(planId),
}))

export type PlanPreviewPath = {
  planId: PlanId
  d: string
  emphasized: boolean
  strokeWidth: number
  dash: string | undefined
}

export type PlanPreviewFan = {
  isMounted: boolean
  paths: PlanPreviewPath[]
  // Screen polylines per fan line — the dither dot-size gradient's
  // distance targets; same control points the drawn paths curve through.
  exclusionPolylines: Array<Array<[number, number]>>
  letters: Array<{ planId: PlanId; x: number; y: number }>
}

type PlanPreviewFanInputs = {
  isShown: boolean
  emphasizedPlan: PlanId
  proj: LineProjection
  bleedStartT: number
  bleedEndT: number
  viewEndT: number
  plot: StripPlotArea
  avoidYearPill: boolean
}

// Fan paths + letter anchors, rebuilt each frame with the rest of the
// strip (5 short paths — cheap) so they track the frame's camera.
export const usePlanPreviewFan = (inputs: PlanPreviewFanInputs): PlanPreviewFan => {
  const isMounted = useFanMountLatch(inputs.isShown)
  if (!isMounted) {
    return { isMounted, paths: [], exclusionPolylines: [], letters: [] }
  }
  const exclusionPolylines = PLAN_PREVIEW_SERIES.map(({ series }) =>
    buildLineScreenPoints(inputs.proj, fanLineSpan(series, inputs)))
  return {
    isMounted,
    paths: buildFanPaths(inputs),
    exclusionPolylines,
    letters: placeAllPlanLetters(inputs, exclusionPolylines),
  }
}

// Keeps the fan mounted through its fade-out so the <g> has something
// to fade — the strip is a pure function of date and has no
// useFadeInOut; the group's CSS opacity transition + this latch are the
// equivalent.
const useFanMountLatch = (isShown: boolean): boolean => {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    if (isShown) {
      setIsMounted(true)
      return
    }
    const timerId = window.setTimeout(() => setIsMounted(false), PLAN_PREVIEW_FADE_MS)
    return () => window.clearTimeout(timerId)
  }, [isShown])
  return isMounted
}

// One span per plan across the whole window, floored like the active
// line so they share its baseline. No past/future split — the thin
// overlay reads fine solid. B/C/D climb past the frame top within a
// couple years and the svg clips them, exactly as on desktop.
const fanLineSpan = (series: TimeValuePoint[], inputs: PlanPreviewFanInputs): LineSpan => ({
  fromT: inputs.bleedStartT,
  toT: inputs.bleedEndT,
  floorValue: HUD87_CAPABILITY_LINE_FLOOR_VALUE,
  series,
  valueAt: time => sampleSeriesAt(series, time),
})

const buildFanPaths = (inputs: PlanPreviewFanInputs): PlanPreviewPath[] =>
  PLAN_PREVIEW_SERIES
    .map(({ planId, series }) => stylePlanPath(planId, series, inputs))
    // Emphasized paints last (on top of the others).
    .sort((a, b) => Number(a.emphasized) - Number(b.emphasized))

const stylePlanPath = (
  planId: PlanId,
  series: TimeValuePoint[],
  inputs: PlanPreviewFanInputs,
): PlanPreviewPath => {
  const emphasized = planId === inputs.emphasizedPlan
  return {
    planId,
    d: buildLinePath(inputs.proj, fanLineSpan(series, inputs)),
    emphasized,
    strokeWidth: emphasized
      ? PLAN_PREVIEW_EMPHASIZED_STROKE_PX
      : PLAN_PREVIEW_LINE_STROKE_PX,
    dash: emphasized ? undefined : PLAN_PREVIEW_DASH,
  }
}

// Hand-tuned vertical nudges applied AFTER the clearance solver settles
// (negative = up); the solver's uniform gap reads slightly tight on A.
const PLAN_LETTER_Y_NUDGE_PX: Partial<Record<PlanId, number>> = { A: -5 }

// Letter anchors for all five plans (every letter stays mounted so
// switching plans cross-fades). The drawn end is the line's point at the
// window's right edge.
const placeAllPlanLetters = (
  inputs: PlanPreviewFanInputs,
  exclusionPolylines: Array<Array<[number, number]>>,
): Array<{ planId: PlanId; x: number; y: number }> =>
  PLAN_PREVIEW_SERIES.map(({ planId, series }, seriesIndex) => {
    const anchor = placePlanLetter({
      polyline: exclusionPolylines[seriesIndex],
      endX: inputs.proj.capX(inputs.proj.xForTime(inputs.viewEndT)),
      endY: inputs.proj.capY(inputs.proj.yForValue(Math.max(
        sampleSeriesAt(series, inputs.viewEndT),
        HUD87_CAPABILITY_LINE_FLOOR_VALUE,
      ))),
      plot: inputs.plot,
      avoidYearPill: inputs.avoidYearPill,
    })
    return {
      planId,
      x: anchor.x,
      y: anchor.y + (PLAN_LETTER_Y_NUDGE_PX[planId] ?? 0),
    }
  })
